import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';

interface ProtectedRouteProps {
    children: ReactNode;
    requireAuth?: boolean;
    requireOnboarding?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAuth = true, requireOnboarding = false }) => {
    const { isLoggedIn, isSessionLoading, isOnboarding } = useSession();

    if (isSessionLoading) {
        return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
    }

    // Critical Guard: Must be logged in
    if (requireAuth && !isLoggedIn) {
        return <Navigate to="/" replace />;
    }

    // If accessing Onboarding but shouldn't be (already done)
    if (requireOnboarding && !isOnboarding) {
        return <Navigate to="/" replace />;
    }

    // If accessing regular app but needs onboarding
    if (!requireOnboarding && requireAuth && isOnboarding) {
        // We can't easily redirect to "onboarding" URL because it's component-based in UserRoutes/WorkerRoutes
        // But the Routes themselves handle the Onboarding Display.
        // This Logical Guard is mostly for external links or direct navigation
        // We will let the Routes logic handle the "Display" of Onboarding component.
        // So we only return children and let the Page Component decide.
        // BUT if we were strict about URL structure, we would redirect.
        // For now, let's just allow it, because the Parent Route (e.g. UserRoutes) checks "if (isOnboarding) return <OnboardingScreen/>"
        return <>{children}</>;
    }

    return <>{children}</>;
};
